import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  computed,
  signal,
} from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { AppPaths } from 'src/app/app-routing.module'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { floorToMinutes } from 'src/app/shared/utils/date.utils'
import { toSignal } from '@angular/core/rxjs-interop'

const getDefaultTimestamp = () => floorToMinutes(new Date(Date.now()))

@Component({
  selector: 'app-rc-history-control',
  templateUrl: './rc-history-control.component.html',
  styleUrls: ['./rc-history-control.component.scss'],
  standalone: false,
})
export class RcHistoryControlComponent implements OnDestroy, OnInit {
  @Output() historyClosed = new EventEmitter<void>()

  public currentTimestamp: Date | undefined
  private cleanUp$ = new Subject<void>()
  public currentTimespan: number | undefined
  public readonly isLoading = toSignal(this.appState.isLoadingChanged$, { initialValue: false })
  public zoom = signal<number | undefined>(undefined)
  public readonly zoomLevelAllowedForHistory = computed(() => {
    const zoom = this.zoom()
    return zoom !== undefined && zoom >= this.config.history_min_zoom_level
  })
  public initValue = 0

  constructor(
    private appState: AppStateService,
    @Inject(APP_CONFIG) public readonly config: AppConfigModel,
  ) {
    this.evaluateQueryParameters()

    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe(() => {
      this.evaluateQueryParameters()
    })

    this.updateQueryParameters()
  }

  ngOnInit(): void {
    this.evaluateQueryParameters()
    if (!this.currentTimestamp) {
      this.currentTimestamp = getDefaultTimestamp()
    }
    this.currentTimespan = this.appState.getState().timespan ? this.appState.getState().timespan : 0
    // Update query parameters to propagate the timestamp change
    this.updateQueryParameters()
  }

  ngOnDestroy(): void {
    this.currentTimestamp = undefined
    this.currentTimespan = undefined
    this.updateQueryParameters()
    this.cleanUp$.next()
    this.cleanUp$.complete()
  }

  handleReloadClick = () => {
    // Needed because of race condition handleOnTimestampChanged
    setTimeout(() => {
      this.appState.reset({ timestamp: undefined })
      this.updateQueryParameters()
    }, 500)
  }

  handleOnHistoryClosed = () => {
    this.historyClosed.emit()
  }

  handleOnTimestampChanged = (timestamp: Date) => {
    this.currentTimestamp = timestamp
  }

  handleOnTimeSpanChanged(timespan: string) {
    this.currentTimespan = Number(timespan)
    this.updateQueryParameters()
  }

  updateQueryParameters() {
    this.appState.setState(AppPaths.RoadCondition, {
      timestamp: this.currentTimestamp?.toISOString(),
      timespan: this.currentTimespan,
    })
  }

  evaluateQueryParameters() {
    const zoom = this.appState.getState().zoom
    this.zoom.set(zoom !== undefined ? Math.floor(Number(zoom)) : undefined)
    this.currentTimespan = this.appState.getState().timespan
    this.currentTimestamp = this.appState.selectTimestamp()
      ? this.appState.selectTimestamp()
      : this.currentTimestamp
  }
}
